import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { GitPullRequest, ChevronDown, ChevronRight, Loader2, FileCode, Clock } from "lucide-react";
import { SkeletonTable } from "./Skeleton";
import SeverityBadge, { Severity, normalizeSeverity } from "./SeverityBadge";
import { listPrScans, getScanFindings, type PrScan, type PrScanFinding } from "../../api/client";

interface PrScansTabProps {
  repoId: string;
}

function ScanStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ok: "bg-green-500/10 text-green-400 border-green-500/20",
    completed: "bg-green-500/10 text-green-400 border-green-500/20",
    partial: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    running: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    pending: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    error: "bg-red-500/10 text-red-400 border-red-500/20",
    failed: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function ScanFindings({ repoId, scanId }: { repoId: string; scanId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["scan-findings", repoId, scanId],
    queryFn: () => getScanFindings(repoId, scanId),
    staleTime: 60000,
  });

  if (isLoading) return <div className="p-4 flex items-center gap-2 text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading findings...</div>;
  if (error) return <div className="p-4 text-red-400">Failed to load findings</div>;
  if (!data?.findings?.length) return <div className="p-4 text-muted">No findings for this scan</div>;

  return (
    <div className="border-t border-divider">
      <table className="w-full">
        <thead>
          <tr className="border-b border-divider bg-surface2/30">
            <th className="text-left px-4 py-2 text-xs font-medium text-muted uppercase tracking-wide w-24">Severity</th>
            <th className="text-left px-4 py-2 text-xs font-medium text-muted uppercase tracking-wide">Finding</th>
            <th className="text-left px-4 py-2 text-xs font-medium text-muted uppercase tracking-wide w-48">File</th>
            <th className="text-left px-4 py-2 text-xs font-medium text-muted uppercase tracking-wide w-28">Tool</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {data.findings.map((f: PrScanFinding) => (
            <tr key={f.id} className="hover:bg-surface2/30 transition-colors">
              <td className="px-4 py-2.5">
                <SeverityBadge severity={normalizeSeverity(f.severity) as Severity} />
              </td>
              <td className="px-4 py-2.5">
                <div className="text-sm text-foreground">{f.title}</div>
                {f.rule_id && <div className="text-xs text-muted mt-0.5">{f.rule_id}</div>}
              </td>
              <td className="px-4 py-2.5">
                {f.file_path && (
                  <div className="flex items-center gap-1.5 text-xs text-muted">
                    <FileCode className="h-3 w-3 shrink-0" />
                    <span className="truncate max-w-[180px]">{f.file_path}</span>
                    {f.start_line && <span>:{f.start_line}</span>}
                  </div>
                )}
              </td>
              <td className="px-4 py-2.5">
                <span className="text-xs text-muted">{f.tool}</span>
                {f.kind && <span className="text-xs text-muted ml-1">({f.kind})</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PrScansTab({ repoId }: PrScansTabProps) {
  const [expandedScan, setExpandedScan] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["pr-scans", repoId],
    queryFn: () => listPrScans(repoId),
    staleTime: 30000,
  });

  if (isLoading) return <SkeletonTable rows={3} columns={5} />;
  if (error) return (
    <div className="card p-8 text-center">
      <p className="text-red-400">Failed to load PR scans</p>
    </div>
  );

  const scans = data?.scans || [];

  if (scans.length === 0) {
    return (
      <div className="card p-12 text-center">
        <GitPullRequest className="h-12 w-12 text-muted mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No PR scans yet</h3>
        <p className="text-sm text-muted">PR scans will appear here when pull requests are opened on this repository.</p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-divider bg-surface2/30">
        <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
          <GitPullRequest className="h-4 w-4" />
          Recent PR Scans
          <span className="text-xs text-muted">({scans.length})</span>
        </h3>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-divider bg-surface2/50">
            <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide w-10"></th>
            <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide">Commit</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide w-24">Status</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide w-24">Findings</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide w-48">Severity</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide w-28">When</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {scans.map((scan: PrScan) => {
            const isExpanded = expandedScan === scan.id;
            return (
              <React.Fragment key={scan.id}>
                <tr
                  className="hover:bg-surface2/50 transition-colors cursor-pointer"
                  onClick={() => setExpandedScan(isExpanded ? null : scan.id)}
                >
                  <td className="px-4 py-3 text-muted">
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-surface2 px-1.5 py-0.5 rounded text-foreground">
                      {scan.commit_sha?.slice(0, 7)}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    <ScanStatusBadge status={scan.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-foreground">{scan.findings_count}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-xs">
                      {scan.critical > 0 && <span className="text-red-400">{scan.critical}C</span>}
                      {scan.high > 0 && <span className="text-orange-400">{scan.high}H</span>}
                      {scan.medium > 0 && <span className="text-yellow-400">{scan.medium}M</span>}
                      {scan.low > 0 && <span className="text-blue-400">{scan.low}L</span>}
                      {scan.findings_count === 0 && <span className="text-green-400">Clean</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted">
                      <Clock className="h-3 w-3" />
                      {timeAgo(scan.created_at)}
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={6} className="p-0 bg-surface1/50">
                      <ScanFindings repoId={repoId} scanId={scan.id} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
